// src/pages/Regularpaperedit.jsx
import React, { useEffect, useState, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Regularpaper.css';
import { FaBell, FaUserCircle, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const API_BASE = 'https://digital.tifleducation.com/admin/public/api';
const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter';
const LOCAL_STATE_KEY = 'regularPaperState';

// Normalize type labels to consistent keys
const normalizeTypeKey = (label) => {
  if (!label) return '';
  const s = String(label)
    .toLowerCase()
    .replace(/[\.\-\/:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (s.includes('mcq') || s.includes('m c q')) return 'mcq';
  if (s.includes('fill') && s.includes('blank')) return 'fill_in_the_blanks';
  if (s.includes('true') && s.includes('false')) return 'true_false';
  if (s.includes('very') && s.includes('short')) return 'very_short_qa';
  if (s.includes('short') && s.includes('answer') && !s.includes('very')) return 'short_qa';
  if (s.includes('long') && s.includes('answer')) return 'long_qa';
  if (s.includes('matching')) return 'matching_type';
  if (s.includes('diagram') || s.includes('image')) return 'diagram_image_based';

  return s.replace(/\s+/g, '_');
};

const Regularpaperedit = () => {
  const { paperId: paramPaperId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state || {};

  // Paper metadata
  const paperName = state.paperName || '';
  const typeOfPaper = state.typeOfPaper || '';
  const date = state.date || '';
  const duration = state.duration || '';
  const totalMarks = state.totalMarks || '';

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [openChapter, setOpenChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [allowedCounts, setAllowedCounts] = useState({});
  const [boardId, setBoardId] = useState(null);
  const [gradeId, setGradeId] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [paperId, setPaperId] = useState(paramPaperId || state.paperId || null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const toggleDropdown = () => setShowDropdown((prev) => !prev);
  const dropdownRef = useRef(null);
  const [dashboardData, setDashboardData] = useState({
    totalBoard: 0,
    totalGrade: 0,
    totalSubjects: 0,
    totalPapers: 0,
    totalQuestions: 0,
  });
  const [user, setUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
  });

  // Fetch dashboard & user data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get("https://digital.tifleducation.com/admin/public/api/getDashboard", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          setDashboardData({
            totalBoard: res.data.totalBoards || 0,
            totalGrade: res.data.totalGrades || 0,
            totalSubjects: res.data.totalSubjects || 0,
            totalPapers: res.data.totalPapers || 0,
            totalQuestions: res.data.totalQuestions || 0,
          });
          if (res.data.user) {
            setUser({
              id: res.data.user.id,
              name: res.data.user.name || '',
              email: res.data.user.email || '',
              password: '', // empty for editing
            });
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error.response?.data || error);
      }
    };

    fetchDashboardData();
  }, []);
  // Handle save changes
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "https://digital.tifleducation.com/admin/public/api/update-profile",
        {
          name: user.name,
          password: user.password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Profile updated successfully!");

      setIsModalOpen(false);
      setUser({ ...user, password: '' }); // reset password field
    } catch (error) {
      console.error("Error updating profile:", error.response?.data || error);

      toast.error("Failed to update profile");

    }
  };

  const toggleChapter = (index) => setOpenChapter(openChapter === index ? null : index);

  const restoreSelectedQuestionsFromLocal = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
      const normalized = {};
      Object.entries(saved).forEach(([chapterId, types]) => {
        normalized[chapterId] = {};
        Object.entries(types || {}).forEach(([typeKey, arr]) => {
          normalized[chapterId][typeKey] = new Set(Array.isArray(arr) ? arr.map(Number) : []);
        });
      });
      setSelectedQuestions(normalized);
    } catch (e) {
      console.warn('Failed to restore selections from localStorage', e);
    }
  };

  const fetchQuestionsForEdit = async (boardId, gradeId, subjectId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/papers/fetch-questions`,
        { board: boardId, grade: gradeId, subject: subjectId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = res.data || {};
      const chaptersData = Object.entries(data).map(([chapterId, info]) => {
        const typesArray = Object.entries(info.types || {}).map(([typeLabel, questions]) => {
          const key = normalizeTypeKey(typeLabel);
          return {
            label: typeLabel,
            key,
            questions: Array.isArray(questions) ? questions : [],
          };
        });
        return { id: Number(chapterId), name: info.chapter_name, types: typesArray };
      });

      setChapters(chaptersData);
      restoreSelectedQuestionsFromLocal();
    } catch (err) {
      toast.error('Failed to load questions for this subject.');
    }
  };

  useEffect(() => {
    const init = async () => {
      const savedState = JSON.parse(localStorage.getItem(LOCAL_STATE_KEY) || '{}');
      const bId = state.boardId || savedState.boardId;
      const gId = state.gradeId || savedState.gradeId;
      const sId = state.subjectId || savedState.subjectId;
      const counts = state.counts || savedState.counts;

      if (bId && gId && sId) {
        setBoardId(bId);
        setGradeId(gId);
        setSubjectId(sId);

        setAllowedCounts({
          mcq: Number(counts.mcq || 0),
          fill_in_the_blanks: Number(counts.fill_in_the_blanks ?? counts.fill ?? 0),
          true_false: Number(counts.true_false ?? counts.truefalse ?? 0),
          very_short_qa: Number(counts.very_short_qa ?? counts.veryshort ?? 0),
          short_qa: Number(counts.short_qa ?? counts.short ?? 0),
          long_qa: Number(counts.long_qa ?? counts.long ?? 0),
          matching_type: Number(counts.matching_type ?? counts.matching ?? 0),
          diagram_image_based: Number(counts.diagram_image_based ?? counts.diagram ?? 0),
        });

        await fetchQuestionsForEdit(bId, gId, sId);
      } else {
        toast.error('Missing necessary data. Go back to Papers and re-start.');
        navigate('/papers');
      }
    };

    init();
  }, []);

  useEffect(() => {
    if (boardId && gradeId && subjectId) {
      localStorage.setItem(
        LOCAL_STATE_KEY,
        JSON.stringify({ boardId, gradeId, subjectId, counts: allowedCounts })
      );
    }
  }, [boardId, gradeId, subjectId, allowedCounts]);

  const handleSavePaper = () => {
    const normalizedSelectedQuestions = {};

    // Ensure every chapter/type has a Set
    chapters.forEach((ch) => {
      if (!selectedQuestions[ch.id]) selectedQuestions[ch.id] = {};
      normalizedSelectedQuestions[ch.id] = {};

      // Ensure all types exist
      const allTypes = [
        "mcq",
        "fill_in_the_blanks",
        "true_false",
        "very_short_qa",
        "short_qa",
        "long_qa",
        "matching_type",
        "diagram_image_based",
      ];

      allTypes.forEach((typeKey) => {
        if (!selectedQuestions[ch.id][typeKey]) selectedQuestions[ch.id][typeKey] = new Set();
        normalizedSelectedQuestions[ch.id][typeKey] = Array.from(selectedQuestions[ch.id][typeKey]);
      });
    });

    // Save selections in localStorage
    localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(normalizedSelectedQuestions));

    // ----- DEBUG LOG -----
    // console.log("=== Navigating to Previewedit ===");
    // console.log("Paper ID:", paperId);
    // console.log("Paper Name:", paperName);
    // console.log("Type of Paper:", typeOfPaper);
    // console.log("Date:", date);
    // console.log("Duration:", duration);
    // console.log("Total Marks:", totalMarks);
    // console.log("Board ID:", boardId);
    // console.log("Grade ID:", gradeId);
    // console.log("Subject ID:", subjectId);
    // console.log("Allowed Counts:", allowedCounts);
    // console.log("Selected Questions:", normalizedSelectedQuestions);
    // console.log("================================");

    // Redirect to previewedit page
    if (paperId) {
      navigate(`/previewedit/${paperId}`, {
        state: {
          paperId,
          boardId,
          gradeId,
          subjectId,
          counts: allowedCounts,
          selected_questions: normalizedSelectedQuestions,
          paperName,
          typeOfPaper,
          date,
          duration,
          totalMarks,
        },
      });
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="regular-header">
          <div className="regular-welcome-text">
            <a
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const res = await axios.get(`${API_BASE}/papers/${paperId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  const paper = res.data?.data?.paper || {};
                  const counts = {
                    mcq: paper.mcq || 0,
                    fill: paper.fill_in_the_blanks || 0,
                    truefalse: paper.true_false || 0,
                    veryshort: paper.very_short_qa || 0,
                    short: paper.short_qa || 0,
                    long: paper.long_qa || 0,
                    matching: paper.matching_type || 0,
                    diagram: paper.diagram_image_based || 0,
                  };
                  navigate(`/selectregularpaperedit/${paperId}`, {
                    state: { paperId, boardId, gradeId, subjectId, counts },
                  });
                } catch (err) {
                  console.error('Failed to fetch paper counts:', err);
                  toast.error('Failed to load paper counts.');
                }
              }}
              className="back-link"
            >
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="regular-welcome-text">Add Questions - Select ChapterWise</span>
          </div>
          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <div className="user-dropdown-wrapper">
              <FaUserCircle className="icon" onClick={toggleDropdown} />
              {showDropdown && (
                <div className="user-dropdown">
                  <div className='edituser' onClick={() => setIsModalOpen(true)}>
                    <FaUserCircle className="ditusericon" />
                    <span>Edit Profile</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="chapter-list">
          {chapters.map((chapter, index) => (
            <div key={chapter.id} className="chapter-box">
              <div className="chapter-header" onClick={() => toggleChapter(index)}>
                <div><strong>{chapter.name}</strong></div>
                {openChapter === index ? <FaChevronDown /> : <FaChevronRight />}
              </div>
              {openChapter === index && (
                <div className="chapter-options">
                  {chapter.types.map((type) => {
                    const routeMap = {
                      mcq: '/mcqedit',
                      fill_in_the_blanks: '/fill-blanksedit',
                      true_false: '/true-falseedit',
                      very_short_qa: '/veryshort-answersedit',
                      short_qa: '/short-answersedit',
                      long_qa: '/long-answersedit',
                      matching_type: '/matchingedit',
                      diagram_image_based: '/diagramedit',
                    };
                    const route = routeMap[type.key] || '/mcq';

                    return (
                      <div
                        key={type.key}
                        className="chapter-option"
                        onClick={() =>
                          navigate(route, {
                            state: {
                              boardId,
                              gradeId,
                              subjectId,
                              chapterId: chapter.id,
                              typeKey: type.key,
                              paperId,
                              selectedQuestions: selectedQuestions[chapter.id]?.[type.key]
                                ? Array.from(selectedQuestions[chapter.id][type.key])
                                : [],
                            },
                          })
                        }
                        style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                      >
                        <span>{type.label}</span>
                        <span>({type.questions?.length || 0})</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'right', margin: '16px 20px 20px 20px' }}>
          <button onClick={handleSavePaper} className="preview-btn">Save Paper</button>
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>
            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input type="text" value={user.name} onChange={e => setUser({ ...user, name: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>
            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input type="password" value={user.password} onChange={e => setUser({ ...user, password: e.target.value })} style={modalStyles.input} />
            </div>
            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Modal Styles -----
const modalStyles = {
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 },
  modal: { background: "#fff", padding: "2rem", borderRadius: "12px", width: "90%", maxWidth: "400px", position: "relative", boxShadow: "0 4px 15px rgba(0,0,0,0.2)" },
  closeBtn: { position: "absolute", top: "10px", right: "15px", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
  userIcon: { textAlign: "center", fontSize: "4rem", color: "#129255", marginBottom: "1rem" },
  formGroup: { marginBottom: "1rem" },
  input: { width: "100%", padding: "0.5rem 0.8rem", borderRadius: "6px", border: "1px solid #ccc", outline: "none" },
  saveBtn: { background: "#129255", color: "#fff", border: "none", padding: "0.6rem 1.2rem", borderRadius: "6px", cursor: "pointer", fontWeight: "bold" }
};
export default Regularpaperedit;
